/* install-intake.js — 5-step wizard controller for the White Glove
 * Installation intake form.
 *
 * Boot paths:
 *   1. ?order=X&sig=Y present → GET  /intake-validate, skip Step 1.
 *   2. No params             → Show Step 1, manual POST /intake-lookup.
 *
 * Final submit:              → POST /intake-submit with the full payload.
 *
 * Wire URLs come from the hosting section's data-* attributes (see
 * `sections/install-intake-form.liquid`).
 *
 * No build step. Keep to vanilla ES2019 so it runs on every Shopify-
 * supported browser without a transpiler.
 */
(function () {
  'use strict';

  var root = document.querySelector('[data-install-intake]');
  if (!root) return;

  var VALIDATE_URL = root.getAttribute('data-validate-url');
  var LOOKUP_URL = root.getAttribute('data-lookup-url');
  var SUBMIT_URL = root.getAttribute('data-submit-url');

  // ─── DOM refs ────────────────────────────────────────────────────────
  var card = root.querySelector('[data-card]');
  var form = root.querySelector('[data-state="form"]');
  var steps = Array.prototype.slice.call(
    root.querySelectorAll('.install-intake__step-panel')
  );
  var stepperItems = Array.prototype.slice.call(
    root.querySelectorAll('.install-intake__stepper .install-intake__step')
  );
  var backBtn = root.querySelector('[data-back]');
  var nextBtn = root.querySelector('[data-next]');

  var states = {
    loading: root.querySelector('[data-state="loading"]'),
    invalid: root.querySelector('[data-state="invalid"]'),
    alreadySubmitted: root.querySelector('[data-state="already-submitted"]'),
    success: root.querySelector('[data-state="success"]'),
    form: form,
  };

  // ─── Wizard state ────────────────────────────────────────────────────
  var current = 1;
  var totalSteps = 5;
  var order = null; // hydrated payload from validate or lookup

  // Calendar state
  var calMonth = new Date();
  calMonth.setDate(1);
  var rangeStart = null;
  var rangeEnd = null;

  // ─── View state toggles ──────────────────────────────────────────────
  function showState(name) {
    Object.keys(states).forEach(function (key) {
      var el = states[key];
      if (!el) return;
      if (key === name) el.removeAttribute('hidden');
      else el.setAttribute('hidden', '');
    });
  }

  // #how-it-works is the next section, not inside this card. State toggles
  // only hide sibling states here, so the anchor stays in the document.
  // Signed-link boot still changes card height after the browser's first
  // hash scroll; re-apply once that layout has settled.
  function landOnHowItWorks() {
    if (window.location.hash !== '#how-it-works') return;
    var target = document.getElementById('how-it-works');
    if (!target || typeof target.scrollIntoView !== 'function') return;
    target.scrollIntoView();
  }

  function setStep(n) {
    current = Math.max(1, Math.min(totalSteps, n));
    steps.forEach(function (panel) {
      var s = Number(panel.getAttribute('data-step'));
      if (s === current) {
        panel.classList.add('is-active');
        panel.removeAttribute('hidden');
      } else {
        panel.classList.remove('is-active');
        panel.setAttribute('hidden', '');
      }
    });
    stepperItems.forEach(function (li, idx) {
      var n1 = idx + 1;
      li.classList.toggle('is-active', n1 === current);
      li.classList.toggle('is-complete', n1 < current);
    });
    // Back is hidden on step 1 always (nothing before it); also on step 2
    // when we arrived via a signed link (step 1 was skipped entirely).
    backBtn.hidden = current === 1 || (!!orderFromQuery.sig && current === 2);
    nextBtn.textContent = current === totalSteps ? 'Submit request' : 'Continue';
    // Step-specific hydration hooks
    if (current === 5) renderReview();
    if (current === 4) ensureCalendarRendered();
    // Scroll the active panel into view on mobile
    if (card && typeof card.scrollIntoView === 'function') {
      // no-op on desktop-first viewports
    }
  }

  // ─── Boot: signed-link vs manual ─────────────────────────────────────
  var params = new URLSearchParams(window.location.search);
  var orderFromQuery = {
    order: params.get('order'),
    sig: params.get('sig'),
  };

  function boot() {
    if (orderFromQuery.order && orderFromQuery.sig) {
      // Signed link path — skip step 1.
      form.hidden = true;
      showState('loading');
      fetch(
        VALIDATE_URL +
          '?order=' +
          encodeURIComponent(orderFromQuery.order) +
          '&sig=' +
          encodeURIComponent(orderFromQuery.sig),
        { credentials: 'omit' }
      )
        .then(parseJson)
        .then(function (result) {
          if (!result.ok) return showErrorState('INTAKE-401');
          if (result.alreadySubmitted) {
            // result.order still populated on alreadySubmitted — use it for the chip.
            if (result.order) {
              order = Object.assign({}, result.order, {
                id: orderFromQuery.order,
                sig: orderFromQuery.sig,
              });
            }
            hydrateAlreadySubmittedState();
            return showState('alreadySubmitted');
          }
          hydrateOrder(result.order, {
            id: orderFromQuery.order,
            sig: orderFromQuery.sig,
          });
          showState('form');
          // Skip Step 1 since validation already confirmed the link.
          setStep(2);
        })
        .catch(function () {
          showErrorState('INTAKE-000');
        })
        .then(landOnHowItWorks);
    } else {
      // Manual lookup path — show step 1.
      showState('form');
      setStep(1);
    }
  }

  // ─── Lookup by order# only (Step 1 submit) ───────────────────────────
  // NOTE: This is a deliberate relaxation from the earlier email-gated
  // flow — see docs/white-glove-ops.md "lookup auth". We accept that
  // anyone with an order number can start the form; the signed-link path
  // from Flow B remains the primary vector.
  function doLookup() {
    var orderNumber = getFieldValue('orderNumber');

    if (!orderNumber) {
      return showError(1, 'Enter your order number.');
    }

    lockNext(true);
    fetch(LOOKUP_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        orderNumber: orderNumber,
      }),
    })
      .then(parseJson)
      .then(function (result) {
        lockNext(false);
        if (!result.ok) {
          return showError(
            1,
            result.error === 'order_not_found'
              ? "We couldn't find an order with that number. Double-check the number (e.g. #12458) from your confirmation email."
              : 'Something went wrong. Please try again, or email us.'
          );
        }
        if (result.alreadySubmitted) {
          // Hide the form; show the "already submitted" state.
          if (result.order) {
            order = Object.assign({}, result.order, {
              id: result.order.id,
              sig: result.order.sig,
            });
          }
          hydrateAlreadySubmittedState();
          return showState('alreadySubmitted');
        }
        hydrateOrder(result.order, {
          id: result.order.id,
          sig: result.order.sig,
        });
        clearError(1);
        setStep(2);
      })
      .catch(function () {
        lockNext(false);
        showError(1, 'Network error. Check your connection and try again.');
      });
  }

  // ─── Hydrate: stash order + fill hidden fields + render items ────────
  function hydrateOrder(payload, linkInfo) {
    order = Object.assign({}, payload, {
      id: linkInfo.id,
      sig: linkInfo.sig,
      contactPhone: linkInfo.contactPhone || payload.phone || '',
    });

    var hiddenOrder = form.querySelector('[data-field="order"]');
    var hiddenSig = form.querySelector('[data-field="sig"]');
    if (hiddenOrder) hiddenOrder.value = String(order.id || '');
    if (hiddenSig) hiddenSig.value = String(order.sig || '');

    // Order meta fields
    setText('orderName', order.name || '—');
    setText(
      'shippingAddress',
      formatAddress(order.shippingAddress) || '—'
    );
    setText(
      'shippingAddressInline',
      formatAddress(order.shippingAddress, { inline: true }) || 'Same as order'
    );

    // Items list
    renderItems();

    // Pre-check flexible time window
    var flex = form.querySelector(
      '.install-intake__slot input[value="flexible"]'
    );
    if (flex) flex.checked = true;
  }

  function renderItems() {
    var list = form.querySelector('[data-items-list]');
    if (!list) return;
    list.innerHTML = '';
    (order.lineItems || []).forEach(function (item, idx) {
      var li = document.createElement('li');
      li.className = 'is-checked';
      li.setAttribute('role', 'listitem');
      var checkbox = document.createElement('input');
      checkbox.type = 'checkbox';
      checkbox.checked = true;
      checkbox.setAttribute('data-item-idx', String(idx));

      var thumb = document.createElement('span');
      thumb.className = 'install-intake__item-thumb';
      if (item.image && item.image.url) {
        var img = document.createElement('img');
        // Request a small rendition so we don't ship full-res product photos.
        img.src = item.image.url.indexOf('?') === -1
          ? item.image.url + '?width=160'
          : item.image.url + '&width=160';
        img.alt = item.image.altText || item.title || '';
        img.loading = 'lazy';
        img.decoding = 'async';
        thumb.appendChild(img);
      } else {
        thumb.setAttribute('data-placeholder', '');
      }

      var body = document.createElement('span');
      body.className = 'install-intake__item-body';
      var title = document.createElement('span');
      title.className = 'install-intake__item-title';
      title.textContent = item.title;
      var qty = document.createElement('span');
      qty.className = 'install-intake__item-qty';
      qty.textContent = 'Qty ' + item.quantity;
      body.appendChild(title);
      body.appendChild(qty);

      li.appendChild(checkbox);
      li.appendChild(thumb);
      li.appendChild(body);
      checkbox.addEventListener('change', function () {
        li.classList.toggle('is-checked', checkbox.checked);
      });
      li.addEventListener('click', function (ev) {
        if (ev.target === checkbox) return;
        checkbox.checked = !checkbox.checked;
        li.classList.toggle('is-checked', checkbox.checked);
      });
      list.appendChild(li);
    });
  }

  // ─── Step 3: alt-address reveal ──────────────────────────────────────
  var altLabel = form.querySelector('[data-alt-address-label]');
  var altInput = form.querySelector('[data-alt-address]');
  form.addEventListener('change', function (ev) {
    if (ev.target && ev.target.name === 'install_location') {
      var isOther = ev.target.value === 'other';
      altLabel.hidden = !isOther;
      altInput.hidden = !isOther;
      if (isOther && altInput) altInput.focus();
    }
  });

  // ─── Step 3: property-contact reveal ─────────────────────────────────
  // "Who will be in the property?" — when "Someone else" is picked we
  // require the contact's name + phone so the installer can reach them
  // directly on the day of the install.
  var propertyContactElse = form.querySelector('[data-property-contact-else]');
  var propertyContactName = form.querySelector('[data-field="propertyContactName"]');
  var propertyContactPhone = form.querySelector('[data-field="propertyContactPhone"]');

  form.addEventListener('change', function (ev) {
    if (ev.target && ev.target.name === 'property_contact') {
      var isElse = ev.target.value === 'someone_else';
      if (propertyContactElse) propertyContactElse.hidden = !isElse;
      if (isElse && propertyContactName) {
        try { propertyContactName.focus(); } catch (e) { /* noop */ }
      } else {
        if (propertyContactName) propertyContactName.value = '';
        if (propertyContactPhone) propertyContactPhone.value = '';
      }
    }
  });

  // ─── Step 3: photos handling (previews + per-photo removal) ──────────
  // Photos are required now — we can't proceed without at least 1 attached.
  // We maintain a mirror `selectedPhotos` array because FileList is immutable;
  // on every change we rebuild a DataTransfer and assign back to `input.files`
  // so native form semantics (required-validation, submit) keep working.
  var photosInput = form.querySelector('[data-photos-input]');
  var photosCount = form.querySelector('[data-photos-count]');
  var photosDrop = form.querySelector('[data-photos-drop]');
  var photosDropTitle = form.querySelector('[data-photos-drop-title]');
  var photosPreviews = form.querySelector('[data-photos-previews]');
  var selectedPhotos = []; // Array<{ file: File, url: string }>

  if (photosInput) {
    photosInput.addEventListener('change', function (ev) {
      var picked = photosInput.files ? Array.prototype.slice.call(photosInput.files) : [];
      picked.forEach(function (file) {
        // Dedupe by name+size+lastModified (cheap fingerprint).
        var dup = selectedPhotos.some(function (p) {
          return (
            p.file.name === file.name &&
            p.file.size === file.size &&
            p.file.lastModified === file.lastModified
          );
        });
        if (dup) return;
        selectedPhotos.push({
          file: file,
          url: URL.createObjectURL(file),
        });
      });
      syncPhotosInput();
      renderPhotoPreviews();
      // Allow picking the same file again later (browsers suppress the change
      // event on unchanged value otherwise).
      photosInput.value = '';
    });
  }

  function syncPhotosInput() {
    if (!photosInput || typeof DataTransfer === 'undefined') return;
    try {
      var dt = new DataTransfer();
      selectedPhotos.forEach(function (p) {
        dt.items.add(p.file);
      });
      photosInput.files = dt.files;
    } catch (err) {
      // Older browsers may not allow DataTransfer write. Fall back to the
      // mirror list — validation uses `selectedPhotos.length` either way.
    }
  }

  function renderPhotoPreviews() {
    if (!photosPreviews) return;
    photosPreviews.innerHTML = '';
    var n = selectedPhotos.length;

    if (n === 0) {
      photosPreviews.hidden = true;
      if (photosCount) photosCount.textContent = 'Required — at least one photo so our crew can plan the install.';
      if (photosDrop) photosDrop.removeAttribute('data-has-photos');
      if (photosDropTitle) photosDropTitle.textContent = 'Tap to add photos';
      return;
    }

    selectedPhotos.forEach(function (p, idx) {
      var li = document.createElement('li');
      li.className = 'install-intake__photo';

      var img = document.createElement('img');
      img.src = p.url;
      img.alt = p.file.name || 'Uploaded photo';
      img.loading = 'lazy';
      img.decoding = 'async';

      var name = document.createElement('span');
      name.className = 'install-intake__photo-name';
      name.textContent = p.file.name || 'photo';

      var remove = document.createElement('button');
      remove.type = 'button';
      remove.className = 'install-intake__photo-remove';
      remove.setAttribute('aria-label', 'Remove ' + (p.file.name || 'photo'));
      remove.textContent = '×';
      remove.addEventListener('click', function (ev) {
        ev.preventDefault();
        ev.stopPropagation();
        removePhotoAt(idx);
      });

      li.appendChild(img);
      li.appendChild(name);
      li.appendChild(remove);
      photosPreviews.appendChild(li);
    });

    photosPreviews.hidden = false;
    if (photosCount) {
      photosCount.textContent =
        n + ' photo' + (n === 1 ? '' : 's') +
        ' attached — we\'ll ask for the originals by email.';
    }
    if (photosDrop) photosDrop.setAttribute('data-has-photos', '');
    if (photosDropTitle) photosDropTitle.textContent = 'Tap to add more';
  }

  function removePhotoAt(idx) {
    var removed = selectedPhotos.splice(idx, 1)[0];
    if (removed && removed.url) {
      try { URL.revokeObjectURL(removed.url); } catch (e) { /* noop */ }
    }
    syncPhotosInput();
    renderPhotoPreviews();
  }

  // Expose a count getter so the rest of the wizard reads from the mirror
  // instead of photosInput.files (which may be out of sync on older browsers).
  function getPhotoCount() {
    return selectedPhotos.length;
  }

  // ─── Step 4: calendar ────────────────────────────────────────────────
  var calTitle = form.querySelector('[data-cal-title]');
  var calDays = form.querySelector('[data-cal-days]');
  var calPrev = form.querySelector('[data-cal-prev]');
  var calNext = form.querySelector('[data-cal-next]');
  var calRendered = false;

  function ensureCalendarRendered() {
    if (!calRendered) {
      calRendered = true;
      // Default to today's month, disable past dates.
      calMonth = new Date();
      calMonth.setDate(1);
    }
    renderCalendar();
  }

  if (calPrev) {
    calPrev.addEventListener('click', function () {
      calMonth = new Date(calMonth.getFullYear(), calMonth.getMonth() - 1, 1);
      renderCalendar();
    });
  }
  if (calNext) {
    calNext.addEventListener('click', function () {
      calMonth = new Date(calMonth.getFullYear(), calMonth.getMonth() + 1, 1);
      renderCalendar();
    });
  }

  function renderCalendar() {
    if (!calDays || !calTitle) return;
    var y = calMonth.getFullYear();
    var m = calMonth.getMonth();
    calTitle.textContent = calMonth.toLocaleDateString(undefined, {
      month: 'long',
      year: 'numeric',
    });

    var firstDay = new Date(y, m, 1).getDay();
    var daysInMonth = new Date(y, m + 1, 0).getDate();
    var today = new Date();
    today.setHours(0, 0, 0, 0);

    calDays.innerHTML = '';
    // Leading empty cells
    for (var i = 0; i < firstDay; i++) {
      var empty = document.createElement('button');
      empty.type = 'button';
      empty.className = 'install-intake__cal-day';
      empty.setAttribute('data-empty', '');
      calDays.appendChild(empty);
    }
    for (var d = 1; d <= daysInMonth; d++) {
      var cell = document.createElement('button');
      cell.type = 'button';
      cell.className = 'install-intake__cal-day';
      var date = new Date(y, m, d);
      date.setHours(0, 0, 0, 0);
      cell.setAttribute('data-date', date.toISOString().slice(0, 10));
      if (date.getTime() < today.getTime()) {
        cell.setAttribute('data-disabled', '');
        cell.disabled = true;
      }
      if (date.getTime() === today.getTime()) {
        cell.setAttribute('data-today', '');
      }
      applyRangeAttrs(cell, date);
      var circle = document.createElement('span');
      circle.className = 'install-intake__cal-circle';
      circle.textContent = String(d);
      cell.appendChild(circle);
      cell.addEventListener('click', function (ev) {
        ev.preventDefault();
        var iso = this.getAttribute('data-date');
        if (!iso || this.hasAttribute('data-disabled')) return;
        onPickDate(new Date(iso + 'T00:00:00'));
      });
      calDays.appendChild(cell);
    }
  }

  function applyRangeAttrs(cell, date) {
    if (!rangeStart) return;
    var start = rangeStart.getTime();
    var end = rangeEnd ? rangeEnd.getTime() : start;
    var t = date.getTime();
    if (t === start) cell.setAttribute('data-range-start', '');
    if (t === end) cell.setAttribute('data-range-end', '');
    if (t > start && t < end) cell.setAttribute('data-in-range', '');
  }

  function onPickDate(date) {
    if (!rangeStart || (rangeStart && rangeEnd)) {
      rangeStart = date;
      rangeEnd = null;
    } else if (date.getTime() < rangeStart.getTime()) {
      rangeStart = date;
      rangeEnd = null;
    } else if (date.getTime() === rangeStart.getTime()) {
      rangeEnd = date;
    } else {
      rangeEnd = date;
    }
    syncPreferredDates();
    renderCalendar();
  }

  function syncPreferredDates() {
    var hidden = form.querySelector('[data-field="preferredDates"]');
    if (!hidden) return;
    if (!rangeStart) return (hidden.value = '');
    if (!rangeEnd || rangeEnd.getTime() === rangeStart.getTime()) {
      hidden.value = fmtDate(rangeStart);
    } else {
      hidden.value = fmtDate(rangeStart) + ' → ' + fmtDate(rangeEnd);
    }
  }

  function fmtDate(d) {
    return d.toLocaleDateString(undefined, {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  }

  // ─── Step 5: review ─────────────────────────────────────────────────
  function renderReview() {
    var review = form.querySelector('[data-review]');
    if (!review) return;

    var selectedItems = getSelectedItems();
    var installLocation = (
      form.querySelector('input[name="install_location"]:checked') || {}
    ).value;
    var altAddress = altInput ? altInput.value.trim() : '';
    var installationDetails = (form.querySelector('[data-installation-details]') || {}).value;
    var preferredDates = getFieldValue('preferredDates');
    var timeWindow = (
      form.querySelector('input[name="time_window"]:checked') || {}
    ).value;
    var propertyContactVal = (
      form.querySelector('input[name="property_contact"]:checked') || {}
    ).value;
    var propertyContactNameVal = propertyContactName ? propertyContactName.value.trim() : '';
    var propertyContactPhoneVal = propertyContactPhone ? propertyContactPhone.value.trim() : '';
    var additionalNotes = (form.querySelector('#install-intake-notes') || {})
      .value;
    var photosN = getPhotoCount();

    var whoText;
    if (propertyContactVal === 'someone_else') {
      whoText = 'Someone else' +
        (propertyContactNameVal ? '\n' + propertyContactNameVal : '') +
        (propertyContactPhoneVal ? ' · ' + propertyContactPhoneVal : '');
    } else if (propertyContactVal === 'me') {
      whoText = 'Me';
    } else {
      whoText = '—';
    }

    var rows = [
      [
        'Order',
        order
          ? order.name +
            '\n' +
            order.customerName +
            ' · ' +
            order.email
          : '—',
      ],
      [
        'Items',
        selectedItems.length
          ? selectedItems
              .map(function (it) {
                return it.quantity + ' × ' + it.title;
              })
              .join('\n')
          : '—',
      ],
      ['Who will be there', whoText],
      [
        'Install at',
        installLocation === 'other'
          ? altAddress || '(alternate address pending)'
          : formatAddress(order && order.shippingAddress) || '—',
      ],
      ['Installation details', installationDetails || '—'],
      [
        'Window',
        (preferredDates || '—') +
          '\n' +
          (labelForTimeWindow(timeWindow) || 'Flexible'),
      ],
      ['Photos', photosN ? photosN + ' attached' : '—'],
      ['Notes', additionalNotes || '—'],
    ];

    review.innerHTML = '';
    rows.forEach(function (row) {
      var d = document.createElement('div');
      var dt = document.createElement('dt');
      dt.textContent = row[0];
      var dd = document.createElement('dd');
      dd.textContent = row[1];
      d.appendChild(dt);
      d.appendChild(dd);
      review.appendChild(d);
    });
  }

  function labelForTimeWindow(v) {
    if (v === 'morning') return 'Morning (8am – 12pm)';
    if (v === 'afternoon') return 'Afternoon (12pm – 4pm)';
    if (v === 'evening') return 'Evening (4pm – 7pm)';
    if (v === 'flexible') return 'Flexible — installer chooses';
    return v || '';
  }

  function getSelectedItems() {
    var boxes = Array.prototype.slice.call(
      form.querySelectorAll('[data-items-list] input[type="checkbox"]')
    );
    return boxes
      .filter(function (b) {
        return b.checked;
      })
      .map(function (b) {
        var idx = Number(b.getAttribute('data-item-idx'));
        var src = (order && order.lineItems && order.lineItems[idx]) || {};
        return { title: src.title || '', quantity: src.quantity || 1 };
      });
  }

  // ─── Step navigation ────────────────────────────────────────────────
  function validateCurrentStep() {
    clearError(current);

    if (current === 1) {
      // Lookup handles its own validation inline.
      return false; // false = don't advance, doLookup advances on success
    }

    if (current === 2) {
      var anyChecked = form.querySelector(
        '[data-items-list] input[type="checkbox"]:checked'
      );
      if (!anyChecked) {
        showError(2, 'Select at least one item to install.');
        return false;
      }
      return true;
    }

    if (current === 3) {
      var propertyContactSel = form.querySelector(
        'input[name="property_contact"]:checked'
      );
      if (!propertyContactSel) {
        showError(3, 'Choose who will be in the property when the installer arrives.');
        return false;
      }
      if (propertyContactSel.value === 'someone_else') {
        var pcName = propertyContactName ? propertyContactName.value.trim() : '';
        var pcPhone = propertyContactPhone ? propertyContactPhone.value.trim() : '';
        if (!pcName) {
          showError(3, 'Add the full name of the person who will be at the property.');
          return false;
        }
        if (!pcPhone) {
          showError(3, 'Add a phone number for the person who will be at the property.');
          return false;
        }
      }
      var sel = form.querySelector(
        'input[name="install_location"]:checked'
      );
      if (sel && sel.value === 'other') {
        if (!altInput.value.trim()) {
          showError(3, 'Enter the alternate install address.');
          return false;
        }
      }
      var installDetails = (form.querySelector('[data-installation-details]') || {}).value;
      if (!installDetails || !installDetails.trim()) {
        showError(3, 'Add the installation details — where we\'re installing, any technical requirements, and how our crew gets in (gate codes, doorman, parking, pets).');
        return false;
      }
      var photosN = getPhotoCount();
      if (photosN < 1) {
        showError(3, 'Please attach at least one photo of the space so our crew can plan the install.');
        return false;
      }
      return true;
    }

    if (current === 4) {
      if (!getFieldValue('preferredDates')) {
        showError(4, 'Pick at least a start date — you can also pick an end date for a range.');
        return false;
      }
      if (!form.querySelector('input[name="time_window"]:checked')) {
        showError(4, 'Choose a time window.');
        return false;
      }
      return true;
    }

    if (current === 5) {
      if (!form.querySelector('[data-consent]').checked) {
        showError(5, 'Please acknowledge the quote-first policy to continue.');
        return false;
      }
      return true;
    }

    return true;
  }

  nextBtn.addEventListener('click', function () {
    if (current === 1) return doLookup();
    if (!validateCurrentStep()) return;
    if (current === totalSteps) return doSubmit();
    setStep(current + 1);
  });

  backBtn.addEventListener('click', function () {
    setStep(current - 1);
  });

  var retryBtn = root.querySelector('[data-retry]');
  if (retryBtn) {
    retryBtn.addEventListener('click', function () {
      var chip = root.querySelector('[data-error-code]');
      if (chip) chip.hidden = true;
      // Re-run boot: signed-link users retry validation; manual users return to step 1.
      boot();
    });
  }

  // ─── Final submit ────────────────────────────────────────────────────
  function doSubmit() {
    if (!order || !order.id || !order.sig) {
      return showError(5, 'Your session lost the order link. Refresh and try again.');
    }
    lockNext(true);
    clearError(5);

    var propertyContact = (
      form.querySelector('input[name="property_contact"]:checked') || {}
    ).value || '';

    var payload = {
      order: String(order.id),
      sig: String(order.sig),
      contact_phone: order.contactPhone || '',
      items: getSelectedItems(),
      install_location: (
        form.querySelector('input[name="install_location"]:checked') || {}
      ).value,
      address: altInput ? altInput.value.trim() : '',
      preferred_dates: getFieldValue('preferredDates'),
      time_window: (
        form.querySelector('input[name="time_window"]:checked') || {}
      ).value,
      property_contact: propertyContact,
      property_contact_name:
        propertyContact === 'someone_else' && propertyContactName
          ? propertyContactName.value.trim()
          : '',
      property_contact_phone:
        propertyContact === 'someone_else' && propertyContactPhone
          ? propertyContactPhone.value.trim()
          : '',
      installation_details: (
        form.querySelector('[data-installation-details]') || {}
      ).value,
      additional_notes: (form.querySelector('#install-intake-notes') || {})
        .value,
      photos_count: getPhotoCount(),
    };

    // Resize photos client-side before upload so we don't hit GCF's 32 MB
    // request cap with full-res iPhone photos, and the crew sees images
    // that load instantly in Pipedrive.
    buildPhotoUploadBlobs()
      .then(function (blobs) {
        var fd = new FormData();
        Object.keys(payload).forEach(function (k) {
          var v = payload[k];
          if (v === undefined || v === null) return;
          if (typeof v === 'object') {
            fd.append(k, JSON.stringify(v));
          } else {
            fd.append(k, String(v));
          }
        });
        blobs.forEach(function (entry) {
          fd.append('photos', entry.blob, entry.filename);
        });
        return fetch(SUBMIT_URL, { method: 'POST', body: fd });
      })
      .then(parseJson)
      .then(function (result) {
        lockNext(false);
        if (!result.ok) {
          if (result.error === 'invalid_signature') {
            return showErrorState('INTAKE-401');
          }
          // Backend errors surface their own code (e.g. shopify_tag_failed); fall back to INTAKE-502.
          return showErrorState('INTAKE-502');
        }
        hydrateSuccessState(result);
        showState('success');
      })
      .catch(function () {
        lockNext(false);
        showErrorState('INTAKE-000');
      });
  }

  // Resize selectedPhotos to max 1600px JPEG q=0.85 for upload. Falls back
  // to the raw File if resizing fails (e.g. HEIC on browsers without HEIC
  // decode — the user's original bytes still ship).
  function buildPhotoUploadBlobs() {
    var MAX_DIM = 1600;
    var QUALITY = 0.85;
    var tasks = selectedPhotos.map(function (entry) {
      return resizePhoto(entry.file, MAX_DIM, QUALITY)
        .then(function (blob) {
          return {
            blob: blob,
            filename: jpgFilenameFor(entry.file),
          };
        })
        .catch(function () {
          return {
            blob: entry.file,
            filename: entry.file.name || 'photo.jpg',
          };
        });
    });
    return Promise.all(tasks);
  }

  function resizePhoto(file, maxDim, quality) {
    return new Promise(function (resolve, reject) {
      if (!file || !file.type || file.type.indexOf('image/') !== 0) {
        return reject(new Error('not_image'));
      }
      var url = URL.createObjectURL(file);
      var img = new Image();
      img.onload = function () {
        try {
          var scale = Math.min(1, maxDim / Math.max(img.width, img.height));
          var w = Math.max(1, Math.round(img.width * scale));
          var h = Math.max(1, Math.round(img.height * scale));
          var canvas = document.createElement('canvas');
          canvas.width = w;
          canvas.height = h;
          var ctx = canvas.getContext('2d');
          ctx.drawImage(img, 0, 0, w, h);
          canvas.toBlob(
            function (blob) {
              URL.revokeObjectURL(url);
              if (!blob) return reject(new Error('toBlob_null'));
              resolve(blob);
            },
            'image/jpeg',
            quality
          );
        } catch (err) {
          URL.revokeObjectURL(url);
          reject(err);
        }
      };
      img.onerror = function () {
        URL.revokeObjectURL(url);
        reject(new Error('image_decode_failed'));
      };
      img.src = url;
    });
  }

  function jpgFilenameFor(file) {
    var base = (file && file.name) ? file.name.replace(/\.[^.]+$/, '') : 'photo';
    return (base || 'photo').slice(0, 80) + '.jpg';
  }

  // ─── State hydration (success chip / already-submitted chip / error) ──
  function hydrateSuccessState(result) {
    var chip = root.querySelector('[data-success-chip]');
    if (!chip) return;
    var dealId =
      result && result.pipedrive && result.pipedrive.dealId
        ? String(result.pipedrive.dealId)
        : '';
    var orderName = (order && order.name) || '';
    var parts = [];
    if (dealId) parts.push('Confirmation #INS-' + dealId);
    if (orderName) parts.push('Order ' + orderName);
    if (!parts.length) return; // no data — leave chip hidden
    chip.textContent = parts.join(' · ');
    chip.hidden = false;
  }

  function hydrateAlreadySubmittedState() {
    var sub = root.querySelector('[data-submitted-sub]');
    var chip = root.querySelector('[data-submitted-chip]');
    if (!order) return;
    if (sub) {
      // We don't store the submission date server-side yet; keep the copy
      // ambiguous enough to read naturally regardless.
      sub.textContent =
        'Your install coordinator is already working on your quote — you\'ll hear from us by email within one business day.';
    }
    if (chip && order.name) {
      chip.textContent = 'Order ' + order.name;
      chip.hidden = false;
    }
    // Link already points to {{ routes.account_url }} in Liquid — no runtime override needed.
  }

  function showErrorState(code) {
    var chip = root.querySelector('[data-error-code]');
    if (chip) {
      chip.textContent = 'Error code: ' + (code || 'INTAKE-500');
      chip.hidden = false;
    }
    showState('invalid');
  }

  // ─── Helpers ────────────────────────────────────────────────────────
  function parseJson(res) {
    return res.json().catch(function () {
      return { ok: false, error: 'parse_error' };
    });
  }

  function lockNext(busy) {
    nextBtn.disabled = !!busy;
    if (busy) {
      nextBtn.setAttribute(
        'data-original-label',
        nextBtn.textContent
      );
      nextBtn.textContent =
        current === totalSteps ? 'Submitting…' : 'Looking up…';
    } else {
      var original = nextBtn.getAttribute('data-original-label');
      nextBtn.textContent =
        original || (current === totalSteps ? 'Submit request' : 'Continue');
    }
  }

  function getFieldValue(fieldName) {
    var el = form.querySelector('[data-field="' + fieldName + '"]');
    return el ? String(el.value || '').trim() : '';
  }

  function setText(fieldName, value) {
    Array.prototype.forEach.call(
      form.querySelectorAll('[data-summary-field="' + fieldName + '"]'),
      function (el) {
        el.textContent = value || '—';
      }
    );
  }

  function showError(step, msg) {
    var el = form.querySelector('[data-error-for="' + step + '"]');
    if (!el) return;
    el.textContent = msg;
    el.hidden = false;
  }

  function clearError(step) {
    var el = form.querySelector('[data-error-for="' + step + '"]');
    if (!el) return;
    el.textContent = '';
    el.hidden = true;
  }

  function formatAddress(addr, opts) {
    if (!addr) return '';
    opts = opts || {};
    if (addr.formatted) {
      return opts.inline ? addr.formatted.replace(/\n/g, ', ') : addr.formatted;
    }
    var lines = [
      addr.address1,
      addr.address2,
      [addr.city, addr.provinceCode, addr.zip].filter(Boolean).join(', '),
      addr.country,
    ].filter(Boolean);
    return opts.inline ? lines.join(', ') : lines.join('\n');
  }

  // ─── Go ─────────────────────────────────────────────────────────────
  boot();
})();
